// FMO Agencies - Agency portfolio with health scores
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, RefreshCw, Search, Users, AlertTriangle,
  TrendingUp, ChevronRight
} from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_AGENCIES_TOUR_STEPS = [
  { type: 'splash', title: 'Agency Portfolio', description: 'Every agency in your network, with health scores based on retention rates. Green border = healthy (90%+). Yellow = needs attention (80-90%). Red = urgent (<80%).' },
  { target: 'fmo-agencies-search', emoji: '🔍', title: 'Search Agencies', description: 'Search by agency name to quickly find any agency in your network.', position: 'bottom' },
  { target: 'fmo-agencies-grid', emoji: '🏢', title: 'Agency Cards', description: 'Each card shows agent count, client count, retention rate, and at-risk count. The left border color indicates health — green, yellow, or red. Click to drill into the agency.', position: 'top' },
  { type: 'celebration', title: 'Know Your Network!', description: 'Check this page weekly. Agencies trending from green to yellow need proactive outreach before they slip into red.' },
];

export default function FmoAgencies() {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [agTourStep, setAgTourStep] = useState(0);
  const [showAgTour, setShowAgTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-agencies-tour-dismissed')) setShowAgTour(true);
  }, []);
  const advanceAgTour = useCallback(() => { setAgTourStep(s => { if (s + 1 >= FMO_AGENCIES_TOUR_STEPS.length) { localStorage.setItem('fmo-agencies-tour-dismissed', 'true'); setShowAgTour(false); return 0; } return s + 1; }); }, []);
  const backAgTour = useCallback(() => setAgTourStep(s => Math.max(0, s - 1)), []);
  const dismissAgTour = useCallback(() => { localStorage.setItem('fmo-agencies-tour-dismissed', 'true'); setShowAgTour(false); }, []);
  useEffect(() => {
    if (!showAgTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissAgTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceAgTour(); if (e.key === 'ArrowLeft') backAgTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showAgTour, advanceAgTour, backAgTour, dismissAgTour]);

  const fetchAgencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/agencies`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setAgencies(json.agencies || []);
    } catch (err) { console.error('[FMO Agencies]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAgencies(); }, [fetchAgencies]);

  const filtered = agencies.filter(a =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A]">Agencies</h1>
              <p className="text-sm text-gray-500">{agencies.length} agencies in your network</p>
            </div>
          </div>
          <button onClick={fetchAgencies} className="p-2 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="relative" data-tour="fmo-agencies-search">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search agencies..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        </div>

        {loading ? (
          <div className="text-center py-12"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400"><Building2 size={48} className="mx-auto mb-3 opacity-30" /><p>No agencies found</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="fmo-agencies-grid">
            {filtered.map(a => {
              const healthColor = a.retentionRate >= 90 ? 'border-green-500' : a.retentionRate >= 80 ? 'border-yellow-500' : 'border-red-500';
              return (
                <Link key={a.id} to={`/fmo/agencies/${a.id}`}
                  className={`bg-white rounded-2xl shadow-sm border-l-4 ${healthColor} border border-gray-200 p-5 hover:shadow-md transition-shadow`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-gray-900 text-lg">{a.name}</h3>
                    <ChevronRight size={18} className="text-gray-300" />
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#172A3A]">{a.agentCount}</p>
                      <p className="text-xs text-gray-400">Agents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-[#172A3A]">{a.activeClients}</p>
                      <p className="text-xs text-gray-400">Clients</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${a.retentionRate >= 90 ? 'text-green-600' : a.retentionRate >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {a.retentionRate}%
                      </p>
                      <p className="text-xs text-gray-400">Retention</p>
                    </div>
                  </div>
                  {a.atRisk > 0 && (
                    <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full w-fit">
                      <AlertTriangle size={12} /> {a.atRisk} at risk
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {showAgTour && (
        <TourBubble stepData={FMO_AGENCIES_TOUR_STEPS[agTourStep]} stepIndex={agTourStep} totalSteps={FMO_AGENCIES_TOUR_STEPS.length}
          onNext={advanceAgTour} onBack={backAgTour} onDismiss={dismissAgTour}
          splashIcon={<Building2 className="w-10 h-10 text-blue-600" />} maskId="fmo-agencies-tour-mask" />
      )}
    </div>
  );
}
